import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { Gem, LoaderCircle } from "lucide-react";
import { useQuestCompletionsV2 } from "../QuestsV2/useQuestCompletions";
import { QuestCard } from "./QuestCard";
import { useQuestFilter } from "./QuestFilterProvider";

interface QuestFilterResultsProps {
  variant?: "default" | "compact";
}

export function QuestFilterResults({
  variant = "default",
}: QuestFilterResultsProps) {
  const { filteredQuests, gameGroups } = useQuestFilter();
  const { completedQuests, mutate: invalidateQuestCompletions } =
    useQuestCompletionsV2();

  const {
    displayedItems: displayedGames,
    hasMore,
    isLoading,
    loadMoreRef,
  } = useInfiniteScroll(gameGroups, { initialItems: 6, itemsPerLoad: 6 });

  if (filteredQuests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-4">
        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center">
          <Gem className="w-10 h-10 text-white/80" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-white">No quests available</h3>
          <p className="text-white/70 max-w-md">
            Check back soon for new quests or try adjusting your filters
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col @container">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h2 className="text-lg md:text-2xl font-bold text-white">
          {gameGroups.length} {gameGroups.length === 1 ? "Game" : "Games"} found
        </h2>
        <p className="text-xs md:text-sm text-white/70 whitespace-nowrap">
          {filteredQuests.length}{" "}
          {filteredQuests.length === 1 ? "Quest" : "Quests"} found
        </p>
      </div>

      <div className="grid gap-4 grid-cols-1 @lg:grid-cols-2 @5xl:grid-cols-3 @[1440px]:grid-cols-4 content-start">
        {displayedGames.map((gameGroup, index) => {
          const completedQuestsForGame = Object.values(
            completedQuests ?? {},
          ).filter((quest) => quest.game_id === gameGroup.gameId);
          return (
            <QuestCard
              completedQuests={completedQuestsForGame}
              invalidateQuestCompletions={invalidateQuestCompletions}
              key={gameGroup.gameId}
              gameName={gameGroup.gameName}
              gameLandingPageUrl={gameGroup.gameLandingPageUrl}
              gameIcon={gameGroup.gameIcon}
              gameSlug={gameGroup.gameSlug}
              gameGenres={gameGroup.gameGenres}
              featured={gameGroup.featured}
              gameId={gameGroup.gameId}
              quests={gameGroup.quests}
              displayOrder={index + 1}
              variant={variant}
            />
          );
        })}
      </div>

      {/* Infinite scroll trigger */}
      {hasMore && (
        <div
          ref={loadMoreRef}
          className="flex items-center justify-center py-8"
        >
          {isLoading && (
            <LoaderCircle className="w-6 h-6 text-white/60 animate-spin" />
          )}
        </div>
      )}
    </div>
  );
}
